import {
  AccountManagementAnalytics,
  AnalyticsDataLayerEvent,
  AnalyticsWebEvent,
  AnalyticsError,
  Commerce,
  EmptyValueDescription,
  Product,
  StoreLocator,
  WebPageDetails,
} from '@/services/analytics/platforms/adobe-analytics/models';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { ServicesContainer } from '@/services';
import { dietaryExclusionTagGroupId as DIETARY_EXCLUSION_TAG_ID } from '../constants';
import { DietaryTagGroup } from '@/models';
import { removeSpecialCharacters, slugify } from '@/helpers';

export class AdobeBaseEvent implements AnalyticsDataLayerEvent {
  storeLocator = {} as StoreLocator;
  account = { devicePlatform: 'web' } as AccountManagementAnalytics;
  commerce = {} as Commerce;
  web = {} as AnalyticsWebEvent;
  error?: AnalyticsError;
  productListItems?: Product[];
  under18?: string;
  optOutPers?: string;

  constructor(store: Store<AppState>, services: ServicesContainer) {
    this.storeLocator.storeId = store.state.restaurant?.selectedRestaurant?.restaurantNumber.toString() ?? EmptyValueDescription.NotChosenYet;

    if (store.state.restaurant.selectedRestaurant && store.state.adobe.ifCustomerSetLocation){
      this.commerce.storeSetMethod = 'customer';
    }
    else if (store.state.restaurant.selectedRestaurant && !store.state.adobe.ifCustomerSetLocation){
      this.commerce.storeSetMethod = 'preset';
    }
    else{
      this.commerce.storeSetMethod = EmptyValueDescription.NotChosenYet;
    }

    const profile = store.state.customer.profile;
    this.account.loginStatus = !!store.getters[CUSTOMER_GETTERS.IS_LOGGED_IN];
    if (this.account.loginStatus && profile) {
      if (profile.customerIdHash && !profile.isGuest) {
        this.account.userID = profile.customerIdHash;
      }
      this.account.isRewards = services.loyalty.isEnrolledInLoyalty(profile);
      this.account.accountCountry = (profile.country) ? profile.country : '(not-chosen-yet)';

      this.account.needsBirthdayGuac = store.getters[CUSTOMER_GETTERS.IS_CYOB_REWARD_ELIGIBLE];

      const isEnrolledInRewards = services.loyalty.isEnrolledInLoyalty(profile);
      const isEnrolledInChipotleU = store.getters.isActiveStudent;
      if(isEnrolledInChipotleU) {
        this.account.loyaltyTier = 'College';
      } else if (isEnrolledInRewards) {
        this.account.loyaltyTier = 'Standard';
      } else {
        this.account.loyaltyTier = 'Unenrolled';
      }
    } else {
      this.account.isRewards = EmptyValueDescription.NotLoggedIn;
      this.account.accountCountry = '(not-received)';
      this.account.loyaltyTier = EmptyValueDescription.NotLoggedIn;
    }

    this.web.webPageDetails = this.extendObject(this.web.webPageDetails, {
      language: window.navigator.language ? window.navigator.language.toLowerCase() : EmptyValueDescription.NotSet,
    } as WebPageDetails);

    this.setDietaryPreferences(store.state.menu.metadata?.dietaryTagGroups, profile?.dietaryTags);

    // '(not-chosen-yet)' to be set if there is no delivery
    // address and there is not a selected restaurant
    if (services.order.isDelivery()) {
      this.commerce.retrievalType = 'Delivery';
      this.commerce.retrievalTypeSet = {value: 1};
      this.commerce.storeSetMethod = '(delivery)';
    } else {
      if (store.state.restaurant.selectedRestaurant) {
        this.commerce.retrievalType = 'Pickup';
        this.commerce.retrievalTypeSet = {value: 1};
      } else {
        this.commerce.retrievalType = EmptyValueDescription.NotChosenYet;
      }
    }
  }

  // Sets dietary preferences for account management analytics. If there are no prefs in the customer profile, set 
  // '(not-chosen-yet)'
  private setDietaryPreferences(tagGroups?: DietaryTagGroup[], customerDietaryPreferences?: string[]): void {
    let exclusions: string[] = [];
    let preferences: string[] = [];
  
    customerDietaryPreferences?.forEach((pref, i) => {
      tagGroups?.forEach(group => 
        group.tags.find(tag => {
          if (tag.tagCode === pref) {
            const tagName = slugify(removeSpecialCharacters(tag.tagName) || '');
            DIETARY_EXCLUSION_TAG_ID === group.id ? exclusions.push(tagName) : preferences.push(tagName);
          } 
        })
      )
    });

    this.account.dietaryPreference = preferences.join(',') || EmptyValueDescription.NotChosenYet;
    this.account.dietaryAvoid = exclusions.join(',') || EmptyValueDescription.NotChosenYet;
  }

  extendObject<T>(target: T, source: Partial<T>): T {
    return {
      ...target,
      ...source,
    };
  }
  overrideObject<T>(source: Partial<T>): Partial<T> {
    return {
      ...source,
    };
  }
}